import { AWSWrapper } from "../aws.js";
import { loadStripe } from '@stripe/stripe-js';
import { StripeConfig } from '../globalVariables.js';

// Modified from https://gist.github.com/peterherrmann/2700284
/**
* Invokes a function, performing up to 3 retries with exponential backoff.
* Retries with delays of approximately 50, 100, then 200 milliseconds for a total of
* about 350 milliseconds before it gives up and rethrows the last error.
* See: https://developers.google.com/google-apps/documents-list/#implementing_exponential_backoff
*
* @param func The anonymous or named function to call.
* @param args args for func.
* @return {*} The value returned by the called function.
*/
export async function callWithRetries(func, ...args) {
  let count = 0;
  while (true) {
    try {
      return func(...args);
    } catch (e) {
      if (count === 3) {
        throw e;
      }
      setTimeout(() => {}, (Math.pow(2, count) * 50) + (Math.round(Math.random() * 50)));
      count += 1;
    }
  }
}

// The Cognito ID token already carries the user's email as a JWT claim, so we
// read it directly instead of making network round-trips to Cognito. Falls back
// to cognito:username, which is the email in this user pool.
export const getUserEmail = (token) => {
  if (!token) {
    return null;
  }
  try {
    const claims = AWSWrapper.parseIdToken(token);
    return claims.email || claims['cognito:username'] || null;
  } catch (err) {
    return null;
  }
}

// Dev/ops impersonation. When set in localStorage, the dashboard reads another
// user's company list / account info using this email, and billing requests
// pass it along so the Lambda can honor it if the caller is in SUPERADMIN_EMAILS.
// Toggle from the browser console:
//   localStorage.setItem('impersonateEmail', 'target@x.com'); location.reload();
//   localStorage.removeItem('impersonateEmail'); location.reload();
export const getImpersonateEmail = () => {
  try {
    return (localStorage.getItem('impersonateEmail') || '').trim().toLowerCase() || null;
  } catch (e) {
    return null;
  }
};

export const getValidSessionEmail = async () => {
  // getFreshIdToken transparently refreshes the ID token via the refresh token
  // when needed, and returns null when there is no valid session. This replaces
  // the previous two sequential Cognito calls (credentialsRefresh +
  // getUserAttributes) with a single, usually-local check.
  const token = await AWSWrapper.getFreshIdToken();
  if (!token) {
    localStorage.removeItem('GStoken');
    return null;
  }

  // Keep the stored token in sync in case it was just refreshed.
  localStorage.setItem('GStoken', token);

  return getUserEmail(token);
}


// Base URL of the funcHandlePayment Lambda (action-dispatch billing endpoint).
const PAYMENT_API_URL = process.env.REACT_APP_PAYMENT_API_BASE_URL ||
  "https://qo2rolglsd.execute-api.us-east-1.amazonaws.com/default/funcHandlePayment-dev";

const currentRootUrl = () => window.location.protocol + '//' + window.location.host;

/**
 * POST an action to the billing Lambda. The Lambda resolves price IDs and the
 * caller's team server-side; the frontend no longer holds any Stripe price IDs.
 *
 * The route is protected by a Cognito User Pool authorizer on API Gateway, so we
 * send the Cognito ID token in the Authorization header. `email` in the body is
 * advisory only — the Lambda trusts the verified `claims.email` from the token.
 * Throws on a non-2xx response or a body carrying an `error` field.
 */
export const callBilling = async (action, params = {}) => {
  // One Cognito getSession() per request — both the body email (advisory) and
  // the Authorization header come from the same fresh token.
  let idToken = await AWSWrapper.getFreshIdToken();
  if (!idToken) {
    localStorage.removeItem('GStoken');
    throw 'Signup first!';
  }
  localStorage.setItem('GStoken', idToken);
  const email = getUserEmail(idToken);
  if (!email) {
    throw 'Signup first!';
  }

  const doFetch = (token) => fetch(PAYMENT_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
    body: JSON.stringify({
      action,
      email,
      rootUrl: currentRootUrl(),
      ...(getImpersonateEmail() ? { impersonateEmail: getImpersonateEmail() } : {}),
      ...params,
    }),
  });

  let res = await doFetch(idToken);

  // If the token expired between mint and request, refresh once and retry.
  if (res.status === 401 || res.status === 403) {
    idToken = await AWSWrapper.getFreshIdToken();
    if (!idToken) {
      throw 'Your session expired. Please log in again.';
    }
    res = await doFetch(idToken);
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.error) {
    if (res.status === 401 || res.status === 403) {
      throw 'Your session expired. Please log in again.';
    }
    throw (data && data.error) || 'Billing request failed';
  }
  return data;
};

/**
 * Start a new subscription via Stripe Checkout for `connections` QBO/Xero
 * connections and `seats` extra user seats. Redirects the browser to Stripe.
 */
export const startCheckout = async (connections, seats = 0) => {
  const lmref = (typeof window.lmFinished === 'function') ? window.lmFinished() : '';
  const { sessionId, url } = await callBilling('start_checkout', { connections, seats, lmref });
  if (url) {
    window.location.href = url;
    return;
  }
  // Fallback: redirect via Stripe.js if the Lambda only returned a session id.
  const stripe = await loadStripe(StripeConfig.PublishableKey);
  const { error } = await stripe.redirectToCheckout({ sessionId });
  if (error) {
    throw error;
  }
};

/** Open the Stripe Customer Portal (manage card, invoices, cancel). */
export const openBillingPortal = async () => {
  const { url } = await callBilling('open_portal');
  window.location.href = url;
};

/** Fetch the full team + subscription summary for the billing page. */
export const getBillingSummary = async () => callBilling('get_billing_summary');

/**
 * Lightweight team status for the dashboard gate (isPaid / connection limits).
 * Skips the Stripe subscription round-trip and the per-member loop that
 * getBillingSummary does, so it returns ~half a second faster on a cold call —
 * the dashboard never uses the `subscription` or `members` fields anyway.
 */
export const getTeamStatus = async () => callBilling('get_team_status');

/** Preview the proration of changing to `connections`/`seats` (no mutation). */
export const previewChange = async (connections, seats) =>
  callBilling('preview_change', { connections, seats });

/** Apply a quantity change to the active subscription. */
export const applyChange = async (connections, seats) =>
  callBilling('apply_change', { connections, seats });

// --- Dashboard cold-start cache ---------------------------------------------- //
// Tier + team + company list keyed by the currently-logged-in email and a 10-min
// TTL. Stored in localStorage so a close-and-reopen browser, a refresh, or a
// new tab can paint the dashboard instantly with the last-known-good values and
// refresh in the background — sidesteps the funcHandlePayment Lambda cold start.
// We tie the cache to the GStoken's email claim so a different user logging in
// on the same browser doesn't see the previous user's data.
const DASHBOARD_CACHE_KEY = 'gs_dashboard_cache_v1';
const DASHBOARD_CACHE_TTL_MS = 10 * 60 * 1000;

export const getCachedDashboardSnapshot = () => {
  try {
    const raw = localStorage.getItem(DASHBOARD_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const { email, ts, snapshot } = parsed || {};
    if (!email || !snapshot || typeof ts !== 'number') return null;
    if (Date.now() - ts > DASHBOARD_CACHE_TTL_MS) return null;
    const current = getUserEmail(localStorage.getItem('GStoken'));
    if (!current || current.toLowerCase() !== email.toLowerCase()) return null;
    return snapshot; // { tier, team, company }
  } catch (e) {
    return null;
  }
};

export const setCachedDashboardSnapshot = (email, snapshot) => {
  if (!email || !snapshot) return;
  try {
    localStorage.setItem(DASHBOARD_CACHE_KEY, JSON.stringify({
      email: String(email).toLowerCase(),
      ts: Date.now(),
      snapshot,
    }));
  } catch (e) {
    // Quota exceeded or storage disabled — non-fatal.
  }
};

export const clearDashboardCache = () => {
  try { localStorage.removeItem(DASHBOARD_CACHE_KEY); } catch (e) { /* non-fatal */ }
};

/** List the team's invited members and current seat usage. */
export const listMembers = async () => callBilling('list_members');

/** Remove a member from the team (unlinks their Account; doesn't delete it). */
export const removeMember = async (email) =>
  callBilling('remove_member', { inviteEmail: email });

/**
 * Backwards-compatible shim for legacy call sites (Login, Signup, Dashboard).
 * All paid plans are now connection-based volume pricing, so any paid tier
 * routes into the new checkout with the requested connection quantity.
 */
export const handleStripe = async (tier, _isMonthly = true, quantity = 1) => {
  if (tier === 'free') {
    return;
  }
  if (tier === 'enterprise') {
    window.open('mailto:info@geniussheets.us?Subject=Interested in using Genius Sheets');
    return;
  }
  try {
    await startCheckout(quantity, 0);
  } catch (error) {
    console.error("handleStripe failed", error);
    throw 'Try again later or contact admin';
  }
};
